import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemDecorator,
} from '@mui/joy';
import {
  GoogleLogo as Google,
  Check,
  Star,
  TrendUp as TrendingUp,
  Clock as Schedule,
  Star as RateReview,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import api from '../../../lib/api';

interface GBPProfile {
  id: number;
  location_name: string;
  profile_id: string;
  address?: string;
  is_active: boolean;
}

type GBPConnectionStepProps = Record<string, never>;

export const GBPConnectionStep: React.FC<GBPConnectionStepProps> = () => {
  const [searchParams] = useSearchParams();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check for OAuth return parameters
  useEffect(() => {
    const success = searchParams.get('success');
    const gbpError = searchParams.get('error');

    if (success === 'gbp_connected') {
      setSuccessMessage('Successfully connected to Google Business Profile!');
    } else if (gbpError) {
      setError(`Failed to connect: ${gbpError}`);
    }
  }, [searchParams]);

  // Fetch existing GBP profiles
  const { data: profiles, isLoading } = useQuery({
    queryKey: ['gbp-profiles'],
    queryFn: async () => {
      const response = await api.get<GBPProfile[] | { results: GBPProfile[] }>(
        '/api/marketing/gbp-profiles/'
      );
      return Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
    },
  });

  const isConnected = profiles && profiles.length > 0;
  const activeProfile = profiles?.find((p) => p.is_active);

  const handleConnect = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const response = await api.get<{ oauth_url: string }>(
        '/api/marketing/gbp-profiles/connect/?return_to=setup&step=gbp_connection'
      );

      if (response.data.oauth_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.oauth_url;
      } else {
        setError('Failed to get OAuth URL. Please try again.');
        setIsConnecting(false);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to initiate connection. Please try again.');
      setIsConnecting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" sx={{ mb: 2 }} startDecorator={<Check />}>
          {successMessage}
        </Alert>
      )}

      {/* Connected state */}
      {isConnected && activeProfile ? (
        <Card
          variant="soft"
          color="success"
          sx={{ p: 3, textAlign: 'center', mb: 3 }}
        >
          <Check size={48} color="var(--joy-palette-success-500)" style={{ marginBottom: 8 }} />
          <Typography level="title-lg" sx={{ mb: 1 }}>
            Connected to Google Business Profile
          </Typography>
          <Typography level="body-md" sx={{ color: 'neutral.700' }}>
            {activeProfile.location_name}
          </Typography>
          {activeProfile.address && (
            <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
              {activeProfile.address}
            </Typography>
          )}
        </Card>
      ) : (
        <>
          {/* Benefits list */}
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography level="title-md" sx={{ mb: 2 }}>
              Why Connect Your GBP?
            </Typography>
            <List>
              <ListItem>
                <ListItemDecorator>
                  <Star color="var(--joy-palette-warning-500)" />
                </ListItemDecorator>
                <Box>
                  <Typography level="title-sm">Auto-post project updates</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                    Share completed jobs directly to your Google Business Profile
                  </Typography>
                </Box>
              </ListItem>
              <ListItem>
                <ListItemDecorator>
                  <RateReview color="var(--joy-palette-primary-500)" />
                </ListItemDecorator>
                <Box>
                  <Typography level="title-sm">Track and respond to reviews</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                    Monitor reviews and respond from your ApexOS dashboard
                  </Typography>
                </Box>
              </ListItem>
              <ListItem>
                <ListItemDecorator>
                  <TrendingUp color="var(--joy-palette-success-500)" />
                </ListItemDecorator>
                <Box>
                  <Typography level="title-sm">Monitor listing performance</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                    See how customers find and interact with your business
                  </Typography>
                </Box>
              </ListItem>
              <ListItem>
                <ListItemDecorator>
                  <Schedule color="var(--joy-palette-neutral-500)" />
                </ListItemDecorator>
                <Box>
                  <Typography level="title-sm">Schedule posts in advance</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.600' }}>
                    Plan your GBP content calendar with scheduled posts
                  </Typography>
                </Box>
              </ListItem>
            </List>
          </Card>

          {/* Connect button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              size="lg"
              variant="solid"
              color="primary"
              startDecorator={<Google />}
              onClick={handleConnect}
              loading={isConnecting}
              sx={{ px: 4 }}
            >
              Connect with Google
            </Button>
            <Typography level="body-xs" sx={{ mt: 1.5, color: 'neutral.500' }}>
              You'll be redirected to Google to authorize the connection
            </Typography>
          </Box>
        </>
      )}

    </Box>
  );
};

export default GBPConnectionStep;
